import React, { ReactElement, useCallback, useEffect, forwardRef } from 'react';
import { Box, Text, Textarea } from '@chakra-ui/react';

type BlockInputProps = {
  label?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  showLineBreakHint?: boolean;
  initialRows?: number;
};
const ROW_HEIGHT = 30;

export const BlockInput = forwardRef<HTMLTextAreaElement, BlockInputProps>(
  (
    {
      label,
      name,
      value,
      onChange,
      onKeyDown,
      initialRows = 1,
      placeholder,
      disabled,
      showLineBreakHint = false,
    },
    ref
  ): ReactElement => {
    const autoResize = useCallback(
      (element: HTMLTextAreaElement | null) => {
        if (!element) return;
        element.style.height = 'auto';
        let newHeight = 0;
        if (initialRows === 1) {
          newHeight = Math.max(element.scrollHeight, 44);
        } else {
          newHeight = Math.max(element.scrollHeight, ROW_HEIGHT * initialRows);
        }
        element.style.height = `${newHeight}px`;

        // Safety measure to prevent overflow
        if (element.scrollHeight > element.clientHeight) {
          element.style.height = `${element.scrollHeight}px`;
        }
      },
      [initialRows]
    );

    // Handle initial sizing and value changes
    useEffect(() => {
      const el = typeof ref === 'object' ? ref?.current : null;
      if (!el) return;
      const runResize = (): void => {
        autoResize(el);
      };
      // Defer so the browser has applied the new value and done layout
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(runResize);
      });
      return () => cancelAnimationFrame(id);
    }, [value, autoResize, ref]);

    return (
      <Box display="flex" flexDirection="column" gap="10px" width="100%">
        {label && (
          <Text textStyle="body.s.regular" color="text.primary" mb={0}>
            {/* htmlFor={name} */}
            {label}
          </Text>
        )}

        <Box position="relative">
          <Textarea
            size="figma"
            name={name}
            ref={ref}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            backgroundColor="background.secondary"
            style={{
              fieldSizing: 'content',
              overflow: 'hidden',
            }}
            rows={initialRows}
            borderColor="border.accent"
            borderRadius="10px"
            borderWidth="1px"
            width="100%"
            resize="none"
            _placeholder={{
              color: 'text.quaternary',
              textStyle: 'body.s.regular',
            }}
            _focus={{
              borderColor: 'border.accent.active',
              boxShadow: '0 0 0 1px {colors.green.primary}',
            }}
            _hover={{
              borderColor: 'border.accent.active',
              transition: 'all 0.2s ease-in-out',
            }}
            disabled={disabled}
          />
          {showLineBreakHint && (
            <Text
              position="absolute"
              bottom="8px"
              right="12px"
              color="text.quaternary"
              pointerEvents="none"
              opacity={value ? 0 : 0.7}
              transition="opacity 0.2s"
              _groupHover={{ opacity: '70' }}
              display="flex"
              alignItems="center"
              gap="2xs"
            >
              <Text as="span" fontSize="10px" fontWeight="medium">
                ⇧ + ⏎
              </Text>
              <Text as="span" fontSize="10px" color="text.quaternary">
                Zeilenumbruch
              </Text>
            </Text>
          )}
        </Box>
      </Box>
    );
  }
);
